import { seedEntrants } from "@rally/draw";
import { moveBy } from "./seeding-order.js";
import type { EventEntry } from "./types.js";

/**
 * The arithmetic of an event's seed order, kept out of the seeding screen so it can be tested.
 *
 * The screen holds the seeded list — member ids, seed 1 first — and every button turns it into
 * the next list, which is saved whole through `set_event_seeds`. Saving whole is what makes a
 * swap one write rather than two that briefly put two entries on one seed.
 */

/** The seeded entries, seed 1 first. Gaps a withdrawal left are closed, as a save closes them. */
export function seededList(entries: readonly EventEntry[]): string[] {
  return entries
    .filter((entry) => entry.status === "registered" && entry.seed != null)
    .sort((x, y) => (x.seed as number) - (y.seed as number))
    .map((entry) => entry.memberId);
}

/**
 * Everyone registered, in the order the next draw will use: the seeded list first, then the
 * rest by rating, unrated last. The same `seedEntrants` the draw runs, so the screen cannot
 * disagree with the bracket it produces.
 */
export function drawOrder(
  entries: readonly EventEntry[],
  seeded: readonly string[],
  ratings: ReadonlyMap<string, number>,
): string[] {
  const seedOf = new Map(seeded.map((memberId, at) => [memberId, at + 1]));
  return seedEntrants(
    entries
      .filter((entry) => entry.status === "registered")
      .map((entry) => ({
        memberIds: [entry.memberId],
        rating: ratings.get(entry.memberId) ?? null,
        fixedSeed: seedOf.get(entry.memberId) ?? null,
      })),
  ).map((seed) => seed.memberIds[0] as string);
}

/** Seed an unseeded entry as the next seed. Already seeded: unchanged. */
export function seedNext(seeded: readonly string[], memberId: string): string[] {
  return seeded.includes(memberId) ? [...seeded] : [...seeded, memberId];
}

/** Move a seeded entry one place up (-1) or down (+1). The ends stay where they are. */
export function moveSeed(seeded: readonly string[], memberId: string, delta: -1 | 1): string[] {
  return moveBy(seeded, seeded.indexOf(memberId), delta);
}

/** Take an entry out of the seeds; everyone below it moves up one. */
export function unseed(seeded: readonly string[], memberId: string): string[] {
  return seeded.filter((id) => id !== memberId);
}
